// src/components/PlaceForm.js
import React, { useState } from 'react';
import './PlaceForm.css';

const PlaceForm = ({ trip, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    start_time: '',
    end_time: '',
    rating: 0,
    note: '',
    expense: {
      food: 0,
      accommodation: 0,
      transport: 0,
      entertainment: 0,
      other: 0
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('expense.')) {
      const expenseType = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        expense: {
          ...prev.expense,
          [expenseType]: parseFloat(value) || 0
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 驗證必填欄位
    if (!formData.name || !formData.start_time) {
      setError('請填寫景點名稱和開始時間');
      return;
    }

    // 驗證時間範圍
    const startTime = new Date(formData.start_time);
    const endTime = formData.end_time ? new Date(formData.end_time) : null;
    const tripStart = new Date(trip.start_date);
    const tripEnd = new Date(trip.end_date);

    if (startTime < tripStart || startTime > tripEnd) {
      setError('景點時間必須在行程日期範圍內');
      return;
    }

    if (endTime && endTime <= startTime) {
      setError('結束時間必須晚於開始時間');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onSubmit(formData);
    } catch (error) {
      setError('新增景點失敗，請再試一次');
      console.error('Error adding place:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="place-form-overlay">
      <div className="place-form-container">
        <div className="place-form-header">
          <h2>新增景點</h2>
          <button 
            onClick={onCancel}
            className="close-btn"
          >
            ×
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="place-form">
          <div className="form-group">
            <label htmlFor="name">景點名稱 *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="例如：淺草寺"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="start_time">開始時間 *</label>
              <input
                type="datetime-local"
                id="start_time"
                name="start_time"
                value={formData.start_time}
                onChange={handleInputChange}
                min={trip.start_date}
                max={trip.end_date}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="end_time">結束時間</label>
              <input
                type="datetime-local"
                id="end_time"
                name="end_time"
                value={formData.end_time}
                onChange={handleInputChange}
                min={formData.start_time}
                max={trip.end_date}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="rating">評分</label>
            <select
              id="rating"
              name="rating"
              value={formData.rating}
              onChange={handleInputChange}
            >
              <option value="0">未評分</option>
              <option value="1">1星 - 很差</option>
              <option value="2">2星 - 差</option>
              <option value="3">3星 - 普通</option>
              <option value="4">4星 - 好</option>
              <option value="5">5星 - 很棒</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="note">心得筆記</label>
            <textarea
              id="note"
              name="note"
              value={formData.note}
              onChange={handleInputChange}
              placeholder="記錄你的旅行心得、注意事項或推薦原因..."
              rows="4"
            />
          </div>

          <div className="expense-section">
            <h3>費用分類</h3>
            <div className="expense-grid">
              <div className="expense-item">
                <label htmlFor="expense.food">餐飲 (NT$)</label>
                <input
                  type="number"
                  id="expense.food"
                  name="expense.food"
                  value={formData.expense.food}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                />
              </div>
              <div className="expense-item">
                <label htmlFor="expense.accommodation">住宿 (NT$)</label>
                <input
                  type="number"
                  id="expense.accommodation"
                  name="expense.accommodation"
                  value={formData.expense.accommodation}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                />
              </div>
              <div className="expense-item">
                <label htmlFor="expense.transport">交通 (NT$)</label>
                <input
                  type="number"
                  id="expense.transport"
                  name="expense.transport"
                  value={formData.expense.transport}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                />
              </div>
              <div className="expense-item">
                <label htmlFor="expense.entertainment">娛樂 (NT$)</label>
                <input
                  type="number"
                  id="expense.entertainment"
                  name="expense.entertainment"
                  value={formData.expense.entertainment}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                />
              </div>
              <div className="expense-item">
                <label htmlFor="expense.other">其他 (NT$)</label>
                <input
                  type="number"
                  id="expense.other"
                  name="expense.other"
                  value={formData.expense.other}
                  onChange={handleInputChange}
                  min="0"
                  step="1"
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              onClick={onCancel}
              className="cancel-btn"
            >
              取消
            </button>
            <button 
              type="submit" 
              disabled={loading}
              className="submit-btn"
            >
              {loading ? '新增中...' : '新增景點'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlaceForm;